/* Rotating homepage hero. One instance per [data-hero-rotating] section, each
   reading its own slides from the JSON the section renders, so two on a page
   cannot share state.

   Web Animations API, no library. The lab version ran on Motion; two of its
   bugs came from that dependency (group controls with no .finished, and
   fill:forwards residue pinning a photo at opacity 0), so run() below commits
   an animation's end state to inline style and cancels it — nothing survives
   into the next transition except what was written explicitly. Springs are
   approximated with an overshoot bezier. */
(() => {
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const small = matchMedia('(max-width: 900px)');
  const EASE = {
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0.22, 1, 0.36, 1)',
    inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  };

  function run(el, frames, opts) {
    const anim = el.animate(frames, { fill: 'forwards', ...opts });
    return anim.finished.then(() => {
      const last = frames[frames.length - 1];
      for (const prop in last) el.style[prop] = last[prop];
      anim.cancel();
    }).catch(() => {});
  }

  function init(root) {
    const cfg = JSON.parse(root.querySelector('[data-hero-rotating-config]').textContent);
    const slides = cfg.slides;
    if (slides.length < 2) return;

    const card   = root.querySelector('[data-card]');
    const crumb  = root.querySelector('[data-crumb]');
    const flag   = root.querySelector('[data-flag]');
    const head   = root.querySelector('[data-head]');
    const lede   = root.querySelector('[data-lede]');
    const cta    = root.querySelector('[data-cta]');
    const cta2   = root.querySelector('[data-cta2]');
    const parts  = [crumb, head, lede, root.querySelector('[data-actions]')];
    const frames = [...root.querySelectorAll('[data-frame]')];
    const dots   = [...root.querySelectorAll('[data-dot]')];
    const fills  = dots.map(d => d.querySelector('[data-fill]'));
    const toggle = root.querySelector('[data-toggle]');
    const controls = root.querySelector('.home-hero-rotating__controls');
    const dwell  = Math.max(3, Number(root.dataset.dwell) || 8) * 1000;

    let i = 0, busy = false, progress = null, hovering = false;
    let rotating = root.dataset.autoplay === 'true';

    function paint(n) {
      const s = slides[n];
      root.style.setProperty('--line', s.color);
      /* A white card cannot use the line colour for its own text, so the tone
         flips the whole palette rather than one property. */
      card.setAttribute('data-tone', s.tone || 'dark');
      crumb.childNodes[0].nodeValue = s.crumb;
      if (flag) flag.hidden = !s.flag;
      head.textContent = s.head;
      lede.textContent = s.lede;
      cta.textContent = s.cta;  cta.href = s.ctaUrl;
      cta2.textContent = s.cta2; cta2.href = s.cta2Url;
      cta2.hidden = !s.cta2;
      frames.forEach((f, k) => f.toggleAttribute('data-on', k === n));
      dots.forEach((d, k) => d.setAttribute('aria-current', String(k === n)));
      fills.forEach((f, k) => { if (k !== n) f.style.width = '0%'; });
      card.setAttribute('aria-label', `${n + 1} of ${slides.length}: ${s.name}`);
    }

    /* The bar is the timer: one animation drives the fill and advances on
       finish, so pausing the bar pauses the carousel and the indicator cannot
       drift from what the viewer sees. Identity guard, not truthiness — a
       superseded timer's promise still resolves on cancel. */
    function runProgress() {
      if (progress) { progress.cancel(); progress = null; }
      fills.forEach(f => f.style.width = '0%');
      if (still.matches || small.matches || !rotating) return;
      const mine = fills[i].animate([{ width: '0%' }, { width: '100%' }], { duration: dwell, easing: 'linear', fill: 'forwards' });
      progress = mine;
      /* A timer created while the pointer is already inside must start paused,
         or hover-pause silently stops applying the moment anyone clicks a dot. */
      if (hovering) mine.pause();
      mine.finished.then(() => { if (progress === mine) go((i + 1) % slides.length); }).catch(() => {});
    }

    async function go(n) {
      if (busy || n === i) return;
      busy = true;
      i = n;

      if (still.matches) { paint(n); busy = false; runProgress(); return; }

      const outgoing = frames.find(f => f.hasAttribute('data-on'));
      const incoming = frames[n];

      await Promise.all([
        run(card, [{ opacity: 1, transform: 'translateY(0) scale(1)' }, { opacity: 0, transform: 'translateY(14px) scale(0.975)' }], { duration: 200, easing: EASE.in }),
        ...parts.map(p => run(p, [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-8px)' }], { duration: 200, easing: EASE.in })),
      ]);

      paint(n);

      /* Photograph cross-fades under the card and settles out of a slow
         push-in, so the frame is never dead between lines. */
      run(outgoing, [{ opacity: 1 }, { opacity: 0 }], { duration: 550, easing: EASE.inOut });
      run(incoming, [{ opacity: 0 }, { opacity: 1 }], { duration: 550, easing: EASE.inOut });
      run(incoming, [{ transform: 'scale(1.045)' }, { transform: 'scale(1)' }], { duration: 1100, easing: EASE.out });

      /* Card springs up first, then its contents stagger in behind it — that
         order is what reads as pop rather than as a fade. */
      run(card, [{ opacity: 0, transform: 'translateY(26px) scale(0.965)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 500, easing: EASE.spring });
      await Promise.all(parts.map((p, k) =>
        run(p, [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, delay: k * 55, easing: EASE.spring })));

      busy = false;
      runProgress();
    }

    /* WCAG 2.2.2 and the APG carousel pattern both require an explicit control
       for auto-rotating content; hover and focus pausing is not a substitute.
       aria-live goes polite only once rotation has stopped, so a screen reader
       is not narrating slides that change under it. */
    function setRotating(on) {
      rotating = on;
      if (toggle) {
        toggle.setAttribute('aria-label', on ? toggle.dataset.labelStop : toggle.dataset.labelStart);
        toggle.querySelector('[data-icon-pause]').hidden = !on;
        toggle.querySelector('[data-icon-play]').hidden = on;
      }
      card.setAttribute('aria-live', on ? 'off' : 'polite');
      if (on) runProgress();
      else { if (progress) { progress.cancel(); progress = null; } fills.forEach(f => f.style.width = '0%'); }
    }

    const step = d => go((i + d + slides.length) % slides.length);
    dots.forEach(d => d.addEventListener('click', () => go(Number(d.dataset.dot))));
    root.querySelector('[data-prev]').addEventListener('click', () => step(-1));
    root.querySelector('[data-next]').addEventListener('click', () => step(1));

    if (toggle) {
      toggle.addEventListener('click', () => setRotating(!rotating));
      /* No auto-forwarding on mobile, so nothing to pause there. */
      const gate = () => { toggle.hidden = small.matches || root.dataset.autoplay !== 'true'; runProgress(); };
      gate();
      small.addEventListener('change', gate);
    }

    /* Keyboard focus only. focusin fires when a button is clicked with a mouse
       too, so gating on it alone meant one click on a dot or arrow killed
       autoplay for good — the carousel looked hung. :focus-visible is the
       platform's own answer to "is this focus keyboard-driven". */
    const keyboardFocusInside = () => {
      const el = document.activeElement;
      if (!el || !root.contains(el) || typeof el.matches !== 'function') return false;
      try { return el.matches(':focus-visible'); } catch (e) { return true; }
    };

    /* Hover-pause belongs on the CONTENT, not the whole hero. Bound to the
       section it covered 62% of the viewport, so a pointer resting anywhere on
       screen froze the carousel — it rotated only while you were in another
       window and stopped the moment you looked at it. The card and the controls
       are what someone is actually reading or aiming at; everything else is
       just a big photograph the cursor happens to be over. */
    const hoverZones = [card, controls].filter(Boolean);
    const pointerInZone = () => hoverZones.some(z => z.matches(':hover'));
    hoverZones.forEach((zone) => {
      zone.addEventListener('mouseenter', () => { hovering = true; if (progress) progress.pause(); });
      zone.addEventListener('mouseleave', () => {
        /* Moving between the card and the controls fires leave then enter; ask
           the browser where the pointer actually is rather than guessing. */
        if (pointerInZone()) return;
        hovering = false;
        if (progress && rotating && !keyboardFocusInside()) progress.play();
      });
    });

    /* A backgrounded tab should not burn through the slides unwatched. */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { if (progress) progress.pause(); }
      else if (progress && rotating && !hovering && !keyboardFocusInside()) progress.play();
    });
    /* APG: rotation stops on keyboard focus and does not restart on its own. */
    root.addEventListener('focusin', () => { if (keyboardFocusInside()) setRotating(false); });
    still.addEventListener('change', runProgress);

    paint(0);
    setRotating(rotating);
  }

  document.querySelectorAll('[data-hero-rotating]').forEach(init);
})();
